import React, { useEffect, useState } from 'react';
import Icon from '@/components/Icon';
import { ApiError, api } from '@/lib/api';
import { useUrlState } from '@/lib/useUrlState';
import { useParentShell } from './ParentShell';

/**
 * Оплата по основному договору ученика за выбранный учебный год.
 *
 * Источник — `/api/parent/payments?student_id=&year_id=`. На бэке:
 *   • договор (school | kindergarten) выбираем по году;
 *   • месяцы строим от даты начала до min(дата окончания, год) с учётом
 *     расторжения и пропорции по рабочим дням для частичных месяцев;
 *   • paid/debt/status по каждому месяцу — из `payment_items` (kind =
 *     school_monthly / kg_monthly, period_start попадает в месяц).
 *
 * Кнопка «Оплатить картой / СБП» ведёт на публичную страницу `/pay/{token}`
 * (`agreement.payUrl`). Если токена нет (договор не подписан / расторгнут)
 * — кнопка скрыта.
 */
const URL_SCHEMA = { tab: 'accruals' };

export default function ParentPaymentsPage() {
  const { child, activeYear } = useParentShell();
  const [params, setParams]   = useUrlState(URL_SCHEMA);

  const [data, setData]   = useState(null);
  const [loading, setLoad] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!child || !activeYear) return;
    let cancelled = false;
    setLoad(true); setError(null);
    api.get(`/api/parent/payments?student_id=${child.id}&year_id=${activeYear.id}`)
      .then((res) => { if (!cancelled) setData(res); })
      .catch((e) => { if (!cancelled) setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'); })
      .finally(() => { if (!cancelled) setLoad(false); });
    return () => { cancelled = true; };
  }, [child?.id, activeYear?.id]);

  if (loading) {
    return <div className="pc-body" style={{ color: 'var(--pc-fg-faint)' }}>Загружаем оплату…</div>;
  }
  if (error) {
    return <div className="pc-body" style={{ color: 'var(--pc-warn)' }}>Ошибка: {error}</div>;
  }
  if (!data) return null;

  const { agreement, admissionFee, summary, months = [], extras = [], payments = [], year } = data;

  if (!agreement) {
    return (
      <div className="pc-body">
        <div className="pc-card">
          <div className="pc-card__head">
            <div className="pc-card__title">Оплата</div>
            <span className="pc-card__sub">{year?.label}</span>
          </div>
          <div className="pc-card__body" style={{ color: 'var(--pc-fg-faint)', padding: '14px 0' }}>
            За этот учебный год нет основного договора — оплачивать пока нечего.
          </div>
        </div>
      </div>
    );
  }

  const remaining = summary?.remaining ?? Math.max(0, (summary?.total || 0) - (summary?.paid || 0));
  const isOverdue = (summary?.debt || 0) > 0;
  const fullyPaid = remaining < 1;

  return (
    <div className="pc-body">
      {/* Hero: сумма + прогресс */}
      <div className="pc-pay-hero">
        <PayAmountCard
          summary={summary}
          agreement={agreement}
          remaining={remaining}
          isOverdue={isOverdue}
          fullyPaid={fullyPaid}
        />
        <PayProgressCard
          year={year}
          agreement={agreement}
          admissionFee={admissionFee}
          summary={summary}
        />
      </div>

      {/* Вкладки «Начисления / Платежи» */}
      <div className="pc-card">
        <div className="pc-card__head" style={{ padding: '14px 18px 0', gap: 0, borderBottom: 0 }}>
          <div className="pc-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={params.tab !== 'payments'}
              className={'pc-tab' + (params.tab !== 'payments' ? ' is-active' : '')}
              onClick={() => setParams({ tab: 'accruals' })}
            >
              Начисления
              <span className="pc-tab__count">{months.length}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={params.tab === 'payments'}
              className={'pc-tab' + (params.tab === 'payments' ? ' is-active' : '')}
              onClick={() => setParams({ tab: 'payments' })}
            >
              Платежи
              <span className="pc-tab__count">{payments.length}</span>
            </button>
            <span className="pc-card__sub" style={{ marginLeft: 'auto', alignSelf: 'center' }}>
              {agreement.tariffName ? `Тариф: ${agreement.tariffName}` : `Ежемесячно: ${fmtRub(agreement.monthly)}`}
            </span>
          </div>
        </div>
        <div className="pc-card__body" style={{ padding: '0 4px 8px' }}>
          {params.tab === 'payments'
            ? <PaymentsTable payments={payments} />
            : <AccrualsTable months={months} />}
        </div>
      </div>

      {/* Доп. услуги (extras-договоры) — компактным списком. */}
      {extras.length > 0 && (
        <div className="pc-card">
          <div className="pc-card__head">
            <div className="pc-card__title">Доп. услуги</div>
            <span className="pc-card__sub">{extras.length} {pluralAgreements(extras.length)}</span>
          </div>
          <div className="pc-card__body" style={{ padding: '0 4px 8px' }}>
            {extras.map((e) => (
              <div key={e.id} className="pc-pay-month" style={{ gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                <div>
                  <div className="pc-pay-month__name">Договор № {e.number}</div>
                  <div className="pc-pay-month__sub">
                    с {fmtDate(e.dateStart)}{e.dateEnd ? ` по ${fmtDate(e.dateEnd)}` : ''}
                  </div>
                </div>
                <div className="pc-pay-month__num">Оплачено: {fmtRub(e.paid)}</div>
                <div />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ───── Tabs ──────────────────────────────────────────────────────── */

function AccrualsTable({ months }) {
  if (!months || months.length === 0) {
    return (
      <div style={{ padding: 14, color: 'var(--pc-fg-faint)' }}>
        Для договора нет месячных начислений.
      </div>
    );
  }
  return (
    <div className="pc-pay-months">
      <div className="pc-pay-month" style={{
        fontSize: 11, color: 'var(--pc-fg-faint)',
        textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600,
      }}>
        <div>Месяц</div>
        <div>Начислено</div>
        <div>Оплачено</div>
        <div>Долг</div>
        <div style={{ textAlign: 'center' }}>Статус</div>
      </div>
      {months.map((m) => (
        <div key={m.month} className="pc-pay-month">
          <div>
            <div className="pc-pay-month__name">{capitalize(m.label)}</div>
            <div className="pc-pay-month__sub">{monthSubLine(m)}</div>
          </div>
          <div className="pc-pay-month__num">{fmtRub(m.amount)}</div>
          <div className="pc-pay-month__num">{m.paid > 0 ? fmtRub(m.paid) : '—'}</div>
          <div className="pc-pay-month__num">
            {m.debt > 0 ? <span style={{ color: 'var(--pc-warn)' }}>{fmtRub(m.debt)}</span> : '—'}
          </div>
          <div>
            <span className={'pc-pay-status pc-pay-status--' + m.status}>
              {STATUS_LABEL[m.status] || m.status}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

function PaymentsTable({ payments }) {
  if (!payments || payments.length === 0) {
    return (
      <div style={{ padding: 14, color: 'var(--pc-fg-faint)' }}>
        За этот учебный год платежей пока нет.
      </div>
    );
  }
  return (
    <div className="pc-pay-months">
      <div className="pc-pay-month pc-pay-row--pay" style={{
        fontSize: 11, color: 'var(--pc-fg-faint)',
        textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600,
      }}>
        <div>Дата</div>
        <div>Назначение</div>
        <div>Способ</div>
        <div style={{ textAlign: 'right' }}>Сумма</div>
      </div>
      {payments.map((p) => (
        <div key={p.id} className="pc-pay-month pc-pay-row--pay">
          <div>
            <div className="pc-pay-month__name">{fmtDate(p.date)}</div>
            {p.description && (
              <div className="pc-pay-month__sub">{p.description}</div>
            )}
          </div>
          <div>
            {p.items.map((it, i) => (
              <div key={i} className="pc-pay-month__sub" style={{ marginTop: i === 0 ? 0 : 2 }}>
                <span style={{ color: 'var(--pc-fg)' }}>{KIND_LABEL[it.kind] || it.name}</span>
                {it.periodStart && (
                  <span style={{ color: 'var(--pc-fg-faint)' }}>
                    {' · '}{periodToMonth(it.periodStart)}
                  </span>
                )}
                <span style={{ color: 'var(--pc-fg-faint)' }}>
                  {' · '}{fmtRub(it.amount)}
                </span>
              </div>
            ))}
          </div>
          <div className="pc-pay-month__sub">{providerLabel(p)}</div>
          <div className="pc-pay-month__total" style={{ textAlign: 'right' }}>
            {fmtRub(p.amount)}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ───── Heroes ────────────────────────────────────────────────────── */

function PayAmountCard({ summary, agreement, remaining, isOverdue, fullyPaid }) {
  // Три визуальных состояния:
  //   • fullyPaid — оплачен ВЕСЬ договор (все месяцы + взнос), зелёная карточка
  //     «Всё оплачено», кнопки нет.
  //   • isOverdue — есть просроченные / частично оплаченные месяцы; красная
  //     карточка «К оплате» с линией просрочки + кнопка оплаты.
  //   • остальное (есть upcoming) — нейтральная карточка «Предстоит оплатить»
  //     с датой ближайшего платежа + кнопка.
  if (fullyPaid) {
    return (
      <div className="pc-pay-amount is-paid">
        <div className="pc-pay-amount__label">Всё оплачено</div>
        <div className="pc-pay-amount__big">{fmtRub(summary?.paid || 0)}</div>
        <div className="pc-pay-amount__sub">
          Все месячные счета закрыты до конца учебного года
        </div>
      </div>
    );
  }
  const nextDue = summary?.nextDue;
  const labelTitle = isOverdue ? 'К оплате' : 'Предстоит оплатить';
  const sub = isOverdue
    ? buildOverdueLine(nextDue)
    : (nextDue?.dueDate
        ? `очередной платёж до ${fmtDate(nextDue.dueDate)}`
        : 'счета будут позже');
  return (
    <div className={'pc-pay-amount' + (isOverdue ? '' : ' is-soon')}>
      <div className="pc-pay-amount__label">{labelTitle}</div>
      <div className="pc-pay-amount__big">{fmtRub(remaining)}</div>
      <div className="pc-pay-amount__sub">{sub}</div>
      {agreement.payUrl && (
        <a
          href={agreement.payUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="pc-pay-amount__btn"
          style={{ textDecoration: 'none' }}
        >
          <Icon name="card" size={14} /> Оплатить картой / СБП
        </a>
      )}
    </div>
  );
}

function PayProgressCard({ year, agreement, admissionFee, summary }) {
  const total = summary?.total || 0;
  const paid = summary?.paid || 0;
  const debt = summary?.debt || 0;
  const paidPct = total > 0 ? (paid / total) * 100 : 0;
  const debtPct = total > 0 ? (debt / total) * 100 : 0;

  return (
    <div className="pc-pay-progress">
      <div style={{ fontSize: 13, fontWeight: 600 }}>Год {year?.label || ''}</div>
      <div style={{ fontSize: 11.5, color: 'var(--pc-fg-faint)', marginTop: 2 }}>
        Договор {agreement.number}
        {agreement.date && <> от {fmtDate(agreement.date)}</>}
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--pc-fg-faint)', marginTop: 1 }}>
        {agreement.typeLabel}
      </div>
      {(agreement.dateStart || agreement.dateEnd) && (
        <div style={{ fontSize: 11.5, color: 'var(--pc-fg-faint)', marginTop: 1 }}>
          {agreement.dateStart && <>с {fmtDate(agreement.dateStart)}</>}
          {agreement.dateEnd && <> по {fmtDate(agreement.dateEnd)}</>}
          {agreement.dateTermination && (
            <span style={{ color: 'var(--pc-warn)' }}>
              {' · расторгнут '}{fmtDate(agreement.dateTermination)}
            </span>
          )}
        </div>
      )}

      <div className="pc-pay-progress__bar">
        <div
          className="pc-pay-progress__fill"
          style={{ width: clamp(paidPct) + '%' }}
        />
        {debt > 0 && (
          <div
            className="pc-pay-progress__fill pc-pay-progress__fill--debt"
            style={{ width: clamp(debtPct) + '%' }}
          />
        )}
      </div>

      <div className="pc-pay-progress__legend">
        <span>
          <span className="pc-pay-progress__leg-dot" style={{ background: 'var(--pc-accent)' }} />
          Оплачено: {fmtRub(paid)}
        </span>
        {debt > 0 && (
          <span>
            <span className="pc-pay-progress__leg-dot" style={{ background: 'var(--pc-warn)' }} />
            Долг: {fmtRub(debt)}
          </span>
        )}
        <span style={{ marginLeft: 'auto', color: 'var(--pc-fg-faint)' }}>
          Всего: {fmtRub(total)}
        </span>
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8,
        marginTop: 14, fontSize: 12,
      }}>
        <div>
          <div style={{ color: 'var(--pc-fg-faint)', fontSize: 11 }}>Ежемесячно</div>
          <div style={{ fontWeight: 600, marginTop: 2 }}>{fmtRub(agreement.monthly)}</div>
        </div>
        <div>
          <div style={{ color: 'var(--pc-fg-faint)', fontSize: 11 }}>Вступительный</div>
          <div style={{ fontWeight: 600, marginTop: 2 }}>
            {admissionFee
              ? <>
                  {fmtRub(admissionFee.amount)}{' '}
                  {admissionFee.isPaid && (
                    <span style={{ color: 'var(--pc-accent-strong)', fontSize: 11 }}>✓</span>
                  )}
                </>
              : <span style={{ color: 'var(--pc-fg-faint)' }}>—</span>}
          </div>
          {admissionFee && !admissionFee.isPaid && admissionFee.dueDate && (
            <div style={{ fontSize: 11, color: 'var(--pc-warn)', marginTop: 2 }}>
              до {fmtDate(admissionFee.dueDate)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ───── Helpers ───────────────────────────────────────────────────── */

const STATUS_LABEL = {
  paid:     'Оплачено',
  partial:  'Частично',
  debt:     'Просрочен',
  upcoming: 'Скоро',
};

const KIND_LABEL = {
  school_monthly:        'Обучение (школа)',
  school_admission_fee:  'Вступительный взнос',
  kg_monthly:            'Детсад',
  kg_admission_fee:      'Вступительный взнос',
  extra_lesson:          'Доп. занятие',
  extra_camp:            'Лагерь',
  manual:                'Прочее',
  discount:              'Скидка',
  refund:                'Возврат',
};

const PROVIDER_LABEL = {
  manual:        'вручную',
  bank_transfer: 'банк. перевод',
  cash:          'наличные',
  yookassa:      'YooKassa',
  modulkassa:    'онлайн-касса',
  terminal:      'терминал',
};

function providerLabel(p) {
  const base = PROVIDER_LABEL[p.provider] || p.provider || '';
  if (p.method) return `${base} · ${p.method}`;
  return base || '—';
}

function periodToMonth(iso) {
  const months = ['', 'январь','февраль','март','апрель','май','июнь','июль','август','сентябрь','октябрь','ноябрь','декабрь'];
  const m = /^(\d{4})-(\d{2})/.exec(iso || '');
  if (!m) return '';
  return `${months[Number(m[2])]} ${m[1]}`;
}

function monthSubLine(m) {
  if (m.status === 'paid' && m.paidDate) {
    return `оплачено ${fmtDateTime(m.paidDate)}`;
  }
  if (m.status === 'partial') {
    return `частично · долг ${fmtRub(m.debt)}`;
  }
  if (m.status === 'debt') {
    return m.dueDate ? `просрочен · срок был ${fmtDate(m.dueDate)}` : 'просрочен';
  }
  if (m.status === 'upcoming') {
    return m.dueDate ? `срок ${fmtDate(m.dueDate)}` : 'предстоит';
  }
  return '';
}

function buildOverdueLine(nextDue) {
  if (!nextDue) return 'Требует оплаты';
  const due = nextDue.dueDate ? fmtDate(nextDue.dueDate) : null;
  if (nextDue.daysOverdue > 0) {
    return `просрочен на ${nextDue.daysOverdue} дн.${due ? ' · до ' + due : ''}`;
  }
  return due ? `до ${due}` : 'требует оплаты';
}

function fmtRub(n) {
  const v = Math.round((Number(n) || 0));
  return v.toLocaleString('ru-RU') + ' ₽';
}

function fmtDate(iso) {
  if (!iso) return '';
  // принимаем как 'YYYY-MM-DD', так и 'YYYY-MM-DD HH:MM:SS'
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}

function fmtDateTime(iso) {
  // Для paidDate бэк возвращает datetime — но в UI достаточно даты.
  return fmtDate(iso);
}

function clamp(pct) {
  if (!Number.isFinite(pct)) return 0;
  return Math.max(0, Math.min(100, pct));
}

function capitalize(s) {
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function pluralAgreements(n) {
  const m10 = n % 10, m100 = n % 100;
  if (m100 >= 11 && m100 <= 14) return 'договоров';
  if (m10 === 1) return 'договор';
  if (m10 >= 2 && m10 <= 4) return 'договора';
  return 'договоров';
}
